import { Howl } from 'howler';
import { EventBus } from './EventBus';
import { audioManager } from './AudioManager';

// Sound effect paths - place files in public/audio/sfx/
const SFX_PATHS = {
  // Weapons
  laserFire: '/audio/sfx/laser_fire.mp3',
  chaingunFire: '/audio/sfx/chaingun_fire.mp3',
  reload: '/audio/sfx/reload.mp3',

  // Impacts
  hit: '/audio/sfx/hit.mp3',
  explosion: '/audio/sfx/explosion.mp3',

  // Pickups
  pickup: '/audio/sfx/pickup.mp3',
  healthPickup: '/audio/sfx/health_pickup.mp3',

  // Vehicle
  engine: '/audio/sfx/engine_loop.mp3',
  boost: '/audio/sfx/boost.mp3',
  land: '/audio/sfx/land.mp3',
};

type SoundName = keyof typeof SFX_PATHS;

class SoundEffectsManager {
  private sounds: Map<string, Howl> = new Map();
  private loadedSounds: Set<string> = new Set();
  private engineSound: Howl | null = null;
  private engineId: number | null = null;

  constructor() {
    this.preloadSounds();
    this.setupEventListeners();
  }

  private preloadSounds(): void {
    // Preload all sound effects
    for (const [name, path] of Object.entries(SFX_PATHS)) {
      const sound = new Howl({
        src: [path],
        preload: true,
        volume: 0.5,
        onload: () => {
          this.loadedSounds.add(name);
        },
        onloaderror: () => {
          // Silently fail - sound file doesn't exist yet
        },
      });
      this.sounds.set(name, sound);

      // Special handling for engine loop
      if (name === 'engine') {
        this.engineSound = sound;
        sound.loop(true);
        sound.volume(0.3);
      }
    }
  }

  private setupEventListeners(): void {
    // Weapon fired
    EventBus.on('weapon:fired', (data) => {
      if (data.weaponType === 'Chain Gun') {
        this.play('chaingunFire', 0.3);
      } else {
        this.play('laserFire', 0.5);
      }
    });

    // Reload
    EventBus.on('weapon:reloading', () => {
      this.play('reload', 0.4);
    });

    // Hit/damage
    EventBus.on('projectile:hit', () => {
      this.play('hit', 0.4);
    });

    // Target destroyed (explosion)
    EventBus.on('target:destroyed', () => {
      this.play('explosion', 0.6);
    });

    // Collectible picked up
    EventBus.on('collectible:picked', (data) => {
      if (data.type === 'health') {
        this.play('healthPickup', 0.5);
      } else {
        this.play('pickup', 0.5);
      }
    });

    // Player healed
    EventBus.on('player:healed', () => {
      this.play('healthPickup', 0.5);
    });

    // Bike landed
    EventBus.on('bike:landed', (data) => {
      if (data.airTime > 0.3) {
        this.play('land', 0.4);
      }
    });

    // Bike launched (boost sound)
    EventBus.on('bike:launched', () => {
      this.play('boost', 0.5);
    });

    // Player damaged
    EventBus.on('player:damaged', () => {
      this.play('hit', 0.6);
    });
  }

  public play(name: SoundName, volume?: number): void {
    const sound = this.sounds.get(name);
    if (sound && this.loadedSounds.has(name)) {
      const vol = (volume ?? 0.5) * audioManager.getSfxVolume();
      sound.volume(vol);
      sound.play();
    }
  }

  public startEngine(): void {
    if (this.engineSound && this.loadedSounds.has('engine') && this.engineId === null) {
      this.engineId = this.engineSound.play();
    }
  }

  public stopEngine(): void {
    if (this.engineSound && this.engineId !== null) {
      this.engineSound.stop(this.engineId);
      this.engineId = null;
    }
  }

  public updateEnginePitch(speed: number): void {
    if (this.engineSound && this.engineId !== null) {
      // Adjust pitch based on speed (0.8 to 1.5)
      const normalizedSpeed = Math.min(Math.abs(speed) / 50, 1);
      const pitch = 0.8 + normalizedSpeed * 0.7;
      this.engineSound.rate(pitch, this.engineId);

      // Also adjust volume
      const vol = (0.2 + normalizedSpeed * 0.3) * audioManager.getSfxVolume();
      this.engineSound.volume(vol, this.engineId);
    }
  }

  public destroy(): void {
    this.stopEngine();
    for (const sound of this.sounds.values()) {
      sound.unload();
    }
    this.sounds.clear();
  }
}

// Singleton instance
export const soundEffects = new SoundEffectsManager();
